import { ReportStepKeyType } from 'hooks/Report/step/useUpdateStepList';
import { ColumnDataType, RowDataType } from 'types/common/CommonType';
import {
  DatasetColumnType,
  ReportDatasetType,
  ReportDataType,
  ReportSheetType,
  ReportStepItemType,
  ReportVersionType,
} from 'types/report/ReportDataTypes';
import { SHEET_TYPE } from 'types/report/enum';
import { Util } from 'utils';
import { create } from 'zustand';
import { ChatItemType } from 'types/chatbot/chatbot';

export const initialReportData: ReportDataType = {
  reportName: '',
  reportVersion: 0,
  description: '',
  entityId: '',
  createdDate: '',
  lastModifiedDate: '',
  createdBy: '',
  reportDatasets: [],
  canExport: false,
  sheetList: [
    {
      sheetId: Util.createUUID(),
      sheetType: SHEET_TYPE.CHART,
      sheetName: 'Chart',
      contentsList: [],
      isHide: false,
      filterStates: [],
      canvasConfig: {
        width: 1920,
        height: 1080,
        backgroundColor: '#FFFFFF',
      },
    },
  ],
};

interface ReportDataStore {
  orgReportData: ReportDataType;
  reportData: ReportDataType;
  reportId: string;
  currentVersionId: string;
  reportVersionList: ReportVersionType[];
  selectedSheetList: string[];
  filteredSheetList: string[];
  targetSheet: ReportSheetType | null;
  selectedDatasetMode: boolean;
  reportDatasets: ReportDatasetType[];
  selectedDataset: ReportDatasetType | null;
  reportStepList: ReportStepItemType[];
  lastStepData: ReportStepItemType | null;
  stepUpdateType: ReportStepKeyType | '';
  selectedStep: ReportStepItemType | null;
  reportColumns: DatasetColumnType[];
  copiedSheetData: ReportSheetType[];
  downloadSheetsInfo: {
    sheetList: ReportSheetType[];
    name: string;
    format: 'PDF' | 'JPG' | 'PNG';
    isSeparatePage: boolean;
    pageSize: {
      width: number;
      height: number;
    };
  } | null;
  previewTableInfo: {
    columnData: ColumnDataType[];
    rowData: RowDataType;
  } | null;
  copiedContentsList: ReportSheetType['contentsList'];
  selectedReportInfo: {
    reportName: string;
    lastModifiedDate: string;
  } | null;
  sheetChatList: {
    [sheetId: string]: ChatItemType[];
  } | null;
  canExport: boolean;
  setOrgReportData: (data: ReportDataType) => void;
  setReportData: (data: ReportDataType) => void;
  setReportId: (id: string) => void;
  setCurrentVersionId: (id: string) => void;
  setReportVersionList: (list: ReportVersionType[]) => void;
  setSelectedSheetList: (list: string[]) => void;
  setFilteredSheetList: (list: string[]) => void;
  setTargetSheet: (sheet: ReportSheetType | null) => void;
  setSelectedDatasetMode: (mode: boolean) => void;
  setReportDatasets: (datasets: ReportDatasetType[]) => void;
  setSelectedDataset: (dataset: ReportDatasetType | null) => void;
  setReportStepList: (list: ReportStepItemType[]) => void;
  setLastStepData: (data: ReportStepItemType) => void;
  setStepUpdateType: (type: ReportStepKeyType | '') => void;
  setSelectedStep: (step: ReportStepItemType | null) => void;
  setReportColumns: (columns: DatasetColumnType[]) => void;
  setCopiedSheetData: (data: ReportSheetType[]) => void;
  setDownloadSheetsInfo: (
    info: {
      sheetList: ReportSheetType[];
      name: string;
      format: 'PDF' | 'JPG' | 'PNG';
      isSeparatePage: boolean;
      pageSize: {
        width: number;
        height: number;
      };
    } | null
  ) => void;
  setPreviewTableInfo: (
    info: {
      columnData: ColumnDataType[];
      rowData: RowDataType;
    } | null
  ) => void;
  setCopiedContentsList: (list: ReportSheetType['contentsList']) => void;
  setSelectedReportInfo: (info: { reportName: string; lastModifiedDate: string }) => void;
  setSheetChatList: (sheetId: string, chatList: ChatItemType[]) => void;
  updateReportData: (data: Partial<ReportDataType>) => void;
  resetReportDataStore: () => void;
  setCanExport: (canExport: boolean) => void;
}

const useReportDataStore = create<ReportDataStore>((set, get) => ({
  orgReportData: initialReportData,
  reportData: initialReportData,
  reportId: '',
  currentVersionId: '',
  currentSlicers: [],
  reportVersionList: [],
  selectedSheetList: [],
  filteredSheetList: [],
  selectedContentsList: [],
  targetSheet: null,
  selectedDatasetMode: false,
  selectedDataset: null,
  reportDatasets: [],
  reportStepList: [],
  lastStepData: null,
  stepUpdateType: '',
  selectedStep: null,
  reportColumns: [],
  copiedSheetData: [],
  targetSheetId: '',
  downloadSheetsInfo: {
    sheetList: [],
    name: '',
    format: 'PDF',
    isSeparatePage: false,
    pageSize: {
      width: 1920,
      height: 1080,
    },
  },
  canvasSize: {
    width: 1920,
    height: 1080,
  },
  previewTableInfo: null,
  copiedContentsList: [],
  selectedReportInfo: null,
  sheetChatList: null,
  canExport: false,
  setReportId: (id: string) => set({ reportId: id }),
  setCurrentVersionId: (id: string) => set({ currentVersionId: id }),
  setCopiedSheetData: (data: ReportSheetType[]) => set({ copiedSheetData: data }),
  setOrgReportData: (data: ReportDataType) => set({ orgReportData: data }),
  setReportData: (data: ReportDataType) => {
    set({ orgReportData: data, reportData: data });
  },
  setReportVersionList: (list: ReportVersionType[]) => set({ reportVersionList: list }),
  setSelectedSheetList: (list: string[]) => set({ selectedSheetList: list }),
  setFilteredSheetList: (list: string[]) => set({ filteredSheetList: list }),
  setTargetSheet: (sheet: ReportSheetType | null) => set({ targetSheet: sheet }),
  setSelectedDatasetMode: (mode: boolean) => set({ selectedDatasetMode: mode }),
  setSelectedDataset: (dataset: ReportDatasetType | null) => set({ selectedDataset: dataset }),
  setReportDatasets: (datasets: ReportDatasetType[]) => set({ reportDatasets: datasets }),
  setReportStepList: (list: ReportStepItemType[]) => set({ reportStepList: list }),
  setLastStepData: (data: ReportStepItemType) => set({ lastStepData: data }),
  setStepUpdateType: (type: ReportStepKeyType | '') => set({ stepUpdateType: type }),
  setSelectedStep: (step: ReportStepItemType | null) => set({ selectedStep: step }),
  setReportColumns: (columns: DatasetColumnType[]) => set({ reportColumns: columns }),
  setDownloadSheetsInfo: (
    info: {
      sheetList: ReportSheetType[];
      name: string;
      format: 'PDF' | 'JPG' | 'PNG';
      isSeparatePage: boolean;
      pageSize: {
        width: number;
        height: number;
      };
    } | null
  ) => set({ downloadSheetsInfo: info }),
  setPreviewTableInfo: (
    info: {
      columnData: ColumnDataType[];
      rowData: RowDataType;
    } | null
  ) => set({ previewTableInfo: info }),
  setCopiedContentsList: (list: ReportSheetType['contentsList']) =>
    set({ copiedContentsList: list }),
  setSelectedReportInfo: (info: { reportName: string; lastModifiedDate: string }) =>
    set({ selectedReportInfo: info }),
  setSheetChatList: (sheetId: string, chatList: ChatItemType[]) =>
    set((state) => ({
      sheetChatList: { ...(state.sheetChatList ?? {}), [sheetId]: chatList },
    })),
  updateReportData: (data: Partial<ReportDataType>) => {
    const { reportData } = get();
    set({ reportData: { ...reportData, ...data } });
  },
  resetReportDataStore: () =>
    set({
      orgReportData: initialReportData,
      reportData: initialReportData,
      reportVersionList: [],
      reportId: '',
      currentVersionId: '',
      selectedSheetList: [],
      targetSheet: null,
      selectedDatasetMode: false,
      selectedDataset: null,
      reportDatasets: [],
      reportStepList: [],
      lastStepData: null,
      stepUpdateType: '',
      selectedStep: null,
      reportColumns: [],
      copiedSheetData: [],
      downloadSheetsInfo: null,
      previewTableInfo: null,
      copiedContentsList: [],
      selectedReportInfo: null,
      sheetChatList: null,
    }),
  setCanExport: (canExport: boolean) => set({ canExport: canExport }),
}));

// useReportDataStore.subscribe((state) => {
//   console.log('reportData => ', state.reportData);
// });

export default useReportDataStore;
